import type {
  PopoverContentProps,
  PopoverRootProps,
  PopoverTriggerProps,
} from '@chakra-ui/react';
import {
  Box,
  Popover as ChakraPopover,
  Portal,
  VStack,
} from '@chakra-ui/react';
import type { PositionerProps } from '@chakra-ui/react/dist/types/components/action-bar/namespace';
import { type ReactNode, useState } from 'react';
import { MdFilterList } from 'react-icons/md';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import { useColorMode } from '@/components/ui/color-mode-utils';
import { chartDateFilterOptions } from '@/utils/dateFilters';

export interface CustomPopoverRootProps extends PopoverRootProps {
  style?: React.CSSProperties;
  children: ReactNode;
}

export interface CustomPopoverTriggerProps extends PopoverTriggerProps {
  style?: React.CSSProperties;
  children?: ReactNode;
  asChild?: boolean;
}

export interface CustomPopoverContentProps extends PopoverContentProps {
  style?: React.CSSProperties;
  width?: string | number;
  p?: number;
  bg?: string;
  boxShadow?: string;
  borderRadius?: string;
  zIndex?: number;
  children?: ReactNode;
}

export type DateFilterState = {
  filter: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

const CustomPopover = {
  Positioner: (props: PositionerProps) => <ChakraPopover.Positioner {...props} />,
  Root: (props: CustomPopoverRootProps) => <ChakraPopover.Root {...props} />,
  Trigger: (props: CustomPopoverTriggerProps) => (
    <ChakraPopover.Trigger {...props} />
  ),
  Content: (props: CustomPopoverContentProps) => (
    <ChakraPopover.Content {...props} />
  ),
};

interface ScoreCardDateFilterProps {
  onDateFilterChange: (value: string | null) => void;
  defaultValue?: string;
  className?: string;
}

const ScoreCardDateFilter = ({
  onDateFilterChange,
  defaultValue = 'year_to_date',
  className,
}: ScoreCardDateFilterProps) => {
  const [selectedFilter, setSelectedFilter] = useState<string | null>(
    defaultValue
  );

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const handleFilterSelect = (value: string) => {
    setSelectedFilter(value);
    onDateFilterChange(value);
  };

  return (
    <Box className={className}>
      <CustomPopover.Root positioning={{ placement: 'bottom-end' }}>
        <CustomPopover.Trigger asChild>
          <FaceliftIconButton
            bg={selectedFilter ? 'brand.solidGradient' : 'transparent'}
            border="1px solid"
            _hover={{ bg: 'brand.solidGradient', color: 'white' }}
            _light={{ borderColor: "gray.600", bg: 'white' }}
            _dark={{ borderColor: "white", bg: 'transparent' }}
            title="More filters"
            size={'2xs'}
          >
            <MdFilterList color={isDark ? 'white': 'black'} />
          </FaceliftIconButton>
        </CustomPopover.Trigger>

        <Portal>
          <CustomPopover.Positioner>
            <CustomPopover.Content
              width="100px"
              p={1}
              boxShadow="lg"
              borderRadius="md"
              zIndex={1000}
            >
              <VStack align="stretch">
                {chartDateFilterOptions.filter((option) => option.value !== 'custom').map((option) => (
                  <Box
                    key={option.value}
                    as="button"
                    onClick={() => handleFilterSelect(option.value as string)}
                    px={1.5}
                    py={1}
                    textAlign="left"
                    borderRadius="sm"
                    bg={
                      selectedFilter === option.value
                        ? 'brand.solid'
                        : 'transparent'
                    }
                    color={
                      selectedFilter === option.value ? 'white' : 'inherit'
                    }
                    _hover={{
                      bg:
                        selectedFilter === option.value
                          ? 'brand.solid'
                          : 'gray.100',
                    }}
                    transition="all 0.2s"
                    cursor="pointer"
                    fontSize="sm"
                  >
                    {option.label}
                  </Box>
                ))}
              </VStack>
            </CustomPopover.Content>
          </CustomPopover.Positioner>
        </Portal>
      </CustomPopover.Root>
    </Box>
  );
};

export default ScoreCardDateFilter;